import { Component } from '@theme/component';
import { ThemeEvents, QuickSipAddOnUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * Formats a price given in the shop's smallest currency unit (cents) using
 * the shop's currency. No client-side money formatter exists elsewhere in
 * this theme (prices are always server-rendered via the `money` filter), so
 * this small helper exists only to keep the live-updating add-ons total in
 * sync with selections that haven't round-tripped to the server yet.
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
  const currency = (typeof Theme !== 'undefined' && Theme.currency) || 'USD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(2);
  }
}

/**
 * How long the "Added ✓" confirmation stays on the Add + button before it
 * swaps for the quantity stepper.
 * @constant {number}
 */
const ADD_CONFIRMATION_DURATION = 900;

/**
 * Resolves the main product's add-to-cart form from a `data-section-id`,
 * matching the `id="ProductInformation-{{ section.id }}"` wrapper rendered
 * by blocks/_product-details.liquid. Shared by every component here that
 * needs to read the main product's own price/quantity fields directly
 * (rather than trying to identify "which quantity-selector fired an event"
 * — several unrelated steppers on the page dispatch the same event shape).
 * @param {string | undefined} sectionId
 * @returns {HTMLFormElement | null}
 */
function getProductForm(sectionId) {
  const root = sectionId ? document.getElementById(`ProductInformation-${sectionId}`) : document;
  return root?.querySelector('[data-type="add-to-cart-form"]') ?? null;
}

/**
 * Resolves the whole product-details wrapper from a `data-section-id`, same
 * root as `getProductForm` above but returned un-narrowed. Needed because
 * blocks/quantity.liquid now renders the main quantity stepper outside
 * `blocks/buy-buttons.liquid`'s own `<form>` (linked back to it only via the
 * HTML `form` attribute, not DOM nesting) — so `[name="quantity"]` is no
 * longer a descendant of the form/`<product-form-component>` and a plain
 * `.querySelector` scoped to either of those would miss it entirely.
 * @param {string | undefined} sectionId
 * @returns {HTMLElement | Document}
 */
function getProductInfoRoot(sectionId) {
  return (sectionId && document.getElementById(`ProductInformation-${sectionId}`)) || document;
}

/**
 * One row in the QuickSip Add-ons list. Wraps a `quantity-selector-component`
 * that starts at 0 and carries no `data-cart-line`, so its
 * `QuantitySelectorUpdateEvent` never triggers a live cart update by itself
 * (assets/component-cart-items.js guards on `if (!line) return;`). This
 * component re-broadcasts the current selection as a
 * `quicksip-addon-line:change` event for the grid to collect, and toggles
 * between a compact "Add +" button and the quantity stepper depending on
 * whether anything is currently selected.
 *
 * @typedef {object} AddonCardRefs
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} qtyWrap
 * @extends Component<AddonCardRefs>
 */
class QuickSipAddonCardComponent extends Component {
  /** @type {number | undefined} */
  #revealTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    if (this.#revealTimeout) clearTimeout(this.#revealTimeout);
  }

  /**
   * Handles a click on the "Add +" button. Steps the nested
   * quantity-selector-component from 0 to 1 immediately (so the selection
   * — and the live total — updates right away), but keeps the button
   * showing its "Added ✓" confirmation for a moment before swapping to the
   * quantity stepper, instead of an instant swap.
   * @param {MouseEvent} event
   */
  handleAddClick(event) {
    event.preventDefault();
    const { addButton } = this.refs;
    addButton?.classList.add('is-added');

    const qtySelector = this.querySelector('quantity-selector-component');
    if (qtySelector && typeof qtySelector.increaseQuantity === 'function') {
      qtySelector.increaseQuantity({ target: qtySelector, preventDefault: () => {} });
    }

    if (this.#revealTimeout) clearTimeout(this.#revealTimeout);
    this.#revealTimeout = window.setTimeout(() => {
      addButton?.classList.remove('is-added');
      this.#toggleControls(true);
      this.#revealTimeout = undefined;

      // The button the shopper just focused is now hidden — browsers don't
      // move focus on their own when that happens, so without this a
      // keyboard user's focus would silently fall back to <body>. Land it
      // on the stepper's decrement button, not its number input — the
      // input already carries `on:focus="/selectInputValue"`
      // (assets/component-quantity-selector.js), which auto-selects its
      // text on focus; combined with this unconditional focus() call, that
      // produced a jarring blue text-selection highlight around the
      // quantity the instant it appeared. A button has no text to select.
      const qtySelectorEl = this.querySelector('quantity-selector-component');
      qtySelectorEl?.querySelector('.quantity-minus')?.focus();
    }, ADD_CONFIRMATION_DURATION);
  }

  /** @param {CustomEvent<{quantity: number}>} event */
  #onQuantityChange = (event) => {
    const variantId = this.dataset.variantId;
    const price = Number(this.dataset.price) || 0;
    const quantity = Number(event.detail?.quantity) || 0;

    this.closest('.quicksip-addon-card')?.classList.toggle('is-selected', quantity > 0);

    if (quantity <= 0) {
      // Stepped back down to zero (via the stepper's own minus button) —
      // reset immediately, no confirmation to wait out.
      if (this.#revealTimeout) {
        clearTimeout(this.#revealTimeout);
        this.#revealTimeout = undefined;
      }
      this.refs.addButton?.classList.remove('is-added');
      this.#toggleControls(false);
    }
    // quantity > 0 while already revealed (a later +/- step) needs no
    // visibility change; quantity > 0 right after a fresh Add + click is
    // handled by the timeout in handleAddClick above.

    this.dispatchEvent(
      new CustomEvent('quicksip-addon-line:change', {
        bubbles: true,
        detail: { variantId, price, quantity, productTitle: this.dataset.productTitle },
      })
    );
  };

  /** @param {boolean} selected */
  #toggleControls(selected) {
    const { addButton, qtyWrap } = this.refs;
    if (!addButton || !qtyWrap) return;

    addButton.hidden = selected;
    qtyWrap.hidden = !selected;
  }
}

if (!customElements.get('quicksip-addon-card-component')) {
  customElements.define('quicksip-addon-card-component', QuickSipAddonCardComponent);
}

/**
 * Wraps the whole QuickSip Add-ons list for one product page. Tracks every
 * selected add-on line, writes the combined selection into the main
 * product form's hidden `quicksip_addon_lines` input (read by
 * product-form.js on submit), broadcasts the running add-on total for the
 * buy-buttons area to display, and shows/hides the "Scroll for more" bar
 * over the `<scroll-hint>` list depending on scroll position.
 *
 * @typedef {object} AddonsGridRefs
 * @property {HTMLElement} [addonsFade]
 * @property {HTMLElement} [liveRegion]
 * @extends Component<AddonsGridRefs>
 */
class QuickSipAddonsGridComponent extends Component {
  /** @type {Map<string, {price: number, quantity: number}>} */
  #selections = new Map();

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('quicksip-addon-line:change', this.#onLineChange);

    const list = this.#list;
    if (list) {
      list.addEventListener('scroll', this.#updateScrollCta);
      this.#resizeObserver = new ResizeObserver(this.#updateScrollCta);
      this.#resizeObserver.observe(list);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('quicksip-addon-line:change', this.#onLineChange);
    this.#list?.removeEventListener('scroll', this.#updateScrollCta);
    this.#resizeObserver?.disconnect();
  }

  get #list() {
    return this.querySelector('.quicksip-addons__list');
  }

  /**
   * Keeps the bottom fade in sync with scroll position — relevant only
   * while there's more content below the fold. (The "Scroll for more" bar
   * itself is always shown, not tied to scroll position.)
   */
  #updateScrollCta = () => {
    const list = this.#list;
    const { addonsFade } = this.refs;
    if (!list) return;

    const { scrollTop, scrollHeight, clientHeight } = list;
    const hasOverflow = scrollHeight > clientHeight + 1;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 2;
    const hasMoreBelow = hasOverflow && !atBottom;

    addonsFade?.classList.toggle('is-hidden', !hasMoreBelow);
  };

  /** @param {CustomEvent<{variantId: string, price: number, quantity: number, productTitle?: string}>} event */
  #onLineChange = (event) => {
    const { variantId, price, quantity, productTitle } = event.detail;
    if (!variantId) return;

    const wasSelected = this.#selections.has(variantId);

    if (quantity > 0) {
      this.#selections.set(variantId, { price, quantity });
    } else {
      this.#selections.delete(variantId);
    }

    this.#announce(productTitle, quantity > 0, wasSelected);
    this.#sync();
  };

  /**
   * Announces additions/removals to screen-reader users — the visual "Add +"
   * ↔ stepper swap has no equivalent for anyone not looking at the screen.
   * Only crossing the 0 boundary is announced (not every +/- step while
   * already selected), to avoid spamming.
   * @param {string | undefined} productTitle
   * @param {boolean} isSelected
   * @param {boolean} wasSelected
   */
  #announce(productTitle, isSelected, wasSelected) {
    const { liveRegion } = this.refs;
    if (!liveRegion || !productTitle) return;

    if (isSelected && !wasSelected) {
      liveRegion.textContent = `${productTitle} added`;
    } else if (!isSelected && wasSelected) {
      liveRegion.textContent = `${productTitle} removed`;
    }
  }

  #sync() {
    const lines = [];
    let totalCents = 0;

    this.#selections.forEach(({ price, quantity }, id) => {
      lines.push({ id, quantity });
      totalCents += price * quantity;
    });

    const input = this.#productForm?.querySelector('[name="quicksip_addon_lines"]');
    if (input instanceof HTMLInputElement) {
      input.value = lines.length ? JSON.stringify(lines) : '';
    }

    document.dispatchEvent(new QuickSipAddOnUpdateEvent(totalCents, lines));
  }

  get #productForm() {
    return getProductForm(this.dataset.sectionId);
  }
}

if (!customElements.get('quicksip-addons-grid-component')) {
  customElements.define('quicksip-addons-grid-component', QuickSipAddonsGridComponent);
}

/**
 * Keeps a genuine order-total bar beside the Add to Cart button in sync:
 * the main product's own price × quantity, plus every currently selected
 * extra. Placed in blocks/buy-buttons.liquid, nested inside the same
 * `<product-form-component>` that carries the main product's own price
 * (its `data-product-price` attribute) — the quantity input itself now
 * lives elsewhere (blocks/quantity.liquid, relocated next to the price),
 * so it's read via `getProductInfoRoot` instead of a lookup scoped to this
 * form/component's own DOM subtree.
 *
 * @typedef {object} AddonsTotalRefs
 * @property {HTMLElement} quicksipAddonsTotalWrap
 * @property {HTMLElement} quicksipAddonsTotalSummary
 * @property {HTMLElement} quicksipAddonsTotalPrice
 * @extends Component<AddonsTotalRefs>
 */
class QuickSipAddonsTotalComponent extends Component {
  /** @type {number} */
  #addonCents = 0;

  /** @type {number} */
  #addonUnits = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quicksipAddOnUpdate, this.#onAddonUpdate);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#render);

    // #render was previously purely event-reactive — fine while the bar
    // only ever appeared once an extra was selected, but now that it shows
    // for the single default product too, it needs to render once up front
    // instead of staying hidden until the first interaction happens to fire
    // one of the events above.
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quicksipAddOnUpdate, this.#onAddonUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#render);
  }

  /** @param {QuickSipAddOnUpdateEvent} event */
  #onAddonUpdate = (event) => {
    this.#addonCents = event.detail.totalCents || 0;
    this.#addonUnits = (event.detail.lines || []).reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
    this.#render();
  };

  /**
   * The main product's own price isn't re-read from the DOM on every
   * render — variant changes morph in a new add-to-cart button fragment
   * but don't touch our own `data-product-price` attribute, so keep it in
   * sync here whenever the shopper picks a different variant.
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const newPrice = event.detail.resource?.price;
    if (typeof newPrice === 'number' && this.#productForm) {
      this.#productForm.dataset.productPrice = String(newPrice);
    }
    this.#render();
  };

  get #productForm() {
    return this.closest('product-form-component');
  }

  #render = () => {
    const { quicksipAddonsTotalWrap, quicksipAddonsTotalSummary, quicksipAddonsTotalPrice } = this.refs;
    if (!quicksipAddonsTotalWrap || !quicksipAddonsTotalSummary || !quicksipAddonsTotalPrice) return;

    const form = this.#productForm;
    const basePriceCents = Number(form?.dataset.productPrice) || 0;
    // The quantity input itself is looked up via the whole product-info
    // root, not `form` — blocks/quantity.liquid renders it outside this
    // form's own DOM subtree now (linked back via the HTML `form`
    // attribute, not nesting), so a lookup scoped to just the form/
    // product-form-component would never find it. See getProductInfoRoot.
    const infoRoot = getProductInfoRoot(form?.dataset.sectionId);
    // `Number(x) || 1` would silently turn a genuine 0 back into 1 (0 is
    // falsy in JS) — the main product can legitimately be 0 now, since its
    // stepper renders with min="0" on qualifying pages (blocks/quantity.liquid),
    // so only fall back to 1 when the field is actually missing/unparseable,
    // not when it's really zero.
    const rawQuantity = Number(infoRoot.querySelector('[name="quantity"]')?.value);
    const baseQuantity = Number.isNaN(rawQuantity) ? 1 : rawQuantity;

    const totalUnitsSelected = baseQuantity + this.#addonUnits;

    // Runs regardless of whether the bar itself has anything to show — Add
    // to Cart / Buy it now must be disabled the instant nothing at all is
    // selected (main product included), not just while the bar is hidden.
    this.#updateSubmitState(totalUnitsSelected > 0);

    if (totalUnitsSelected <= 0) {
      // Toggled via a class instead of `hidden` so the CSS grid-rows
      // transition on .quicksip-addons-total-wrap can animate it away
      // smoothly instead of snapping to display:none. Shown for the single
      // default product on its own too (not just once an extra is added) —
      // only actually empty (main product also at 0) hides it.
      quicksipAddonsTotalWrap.classList.remove('is-visible');
      return;
    }

    const baseLineCents = basePriceCents * baseQuantity;

    const totalCents = baseLineCents + this.#addonCents;
    const totalUnits = totalUnitsSelected;
    const formattedPrice = formatMoney(totalCents);
    const priceChanged = quicksipAddonsTotalPrice.textContent !== formattedPrice;

    quicksipAddonsTotalWrap.classList.add('is-visible');
    const itemWord = totalUnits === 1 ? 'item' : 'items';
    quicksipAddonsTotalSummary.textContent = `Subtotal · ${totalUnits} ${itemWord}`;
    quicksipAddonsTotalPrice.textContent = formattedPrice;

    // Brief pulse so a live update reads as "the number just moved" rather
    // than silently jumping. Remove-then-reflow-then-add restarts the CSS
    // animation even if it's already mid-pulse from a rapid prior change.
    if (priceChanged) {
      quicksipAddonsTotalPrice.classList.remove('is-updated');
      void quicksipAddonsTotalPrice.offsetWidth;
      quicksipAddonsTotalPrice.classList.add('is-updated');
    }
  };

  /**
   * Disables Add to Cart / Buy it now the instant there's nothing to
   * submit at all — reachable now that the main product itself can be
   * stepped down to 0, same as any add-on. `add-to-cart-component` already
   * exposes `.enable()`/`.disable()` (used elsewhere for variant
   * availability); the quick-checkout button has no such component, so its
   * `disabled` property is set directly via its stable data attribute.
   * @param {boolean} canSubmit
   */
  #updateSubmitState(canSubmit) {
    const form = this.#productForm;
    if (!form) return;

    const addToCart = form.querySelector('add-to-cart-component');
    if (canSubmit) addToCart?.enable?.(); else addToCart?.disable?.();

    const quickCheckout = form.querySelector('[data-quick-checkout]');
    if (quickCheckout instanceof HTMLButtonElement) {
      quickCheckout.disabled = !canSubmit;
    }
  }
}

if (!customElements.get('quicksip-addons-total-component')) {
  customElements.define('quicksip-addons-total-component', QuickSipAddonsTotalComponent);
}
